import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';

/**
 * Reusable CSS-based confetti celebration component.
 * Generates random confetti pieces with CSS animations (no external libraries).
 * @param {Object} props
 * @param {string} props.title - Title text to show (default: "Felicidades!")
 * @param {string} props.subtitle - Subtitle text
 * @param {number} props.durationMs - How long confetti falls before cleanup (default: 4000)
 * @param {number} props.pieceCount - Number of confetti pieces (default: 60)
 * @param {Function} props.onDone - Callback when celebration ends
 */
const TutorialCelebration = ({
  title = 'Felicidades!',
  subtitle = '',
  durationMs = 4000,
  pieceCount = 60,
  onDone,
  showContinueButton = true,
}) => {
  const colors = useMemo(
    () => [
      '#22d3ee', '#a855f7', '#ec4899', '#f59e0b',
      '#10b981', '#3b82f6', '#f43f5e', '#8b5cf6',
    ],
    []
  );

  const pieces = useMemo(() => {
    return Array.from({ length: pieceCount }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 6 + 6, // 6-12px
      delay: Math.random() * 1.5,
      duration: Math.random() * 2 + 2.5, // 2.5-4.5s
      rotation: Math.random() * 360,
    }));
  }, [pieceCount, colors]);

  useEffect(() => {
    if (!durationMs) return;
    const timer = setTimeout(() => {
      if (typeof onDone === 'function') onDone();
    }, durationMs);
    return () => clearTimeout(timer);
  }, [durationMs, onDone]);

  return (
    <div className="tutorial-celebration">
      <div className="tutorial-confetti-container">
        {pieces.map((p) => (
          <div
            key={p.id}
            className="tutorial-confetti-piece"
            style={{
              left: p.left,
              width: `${p.size}px`,
              height: `${p.size * 1.6}px`,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              transform: `rotate(${p.rotation}deg)`,
            }}
          />
        ))}
      </div>

      <motion.div
        className="tutorial-celebration-text"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      >
        <div>🎉 {title} 🎉</div>
        {subtitle && (
          <div style={{ fontSize: '0.55em', fontWeight: 500, marginTop: 8, opacity: 0.85 }}>
            {subtitle}
          </div>
        )}
        {showContinueButton && (
          <motion.button
            className="tutorial-celebration-continue-btn"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.3 }}
            onClick={() => {
              if (typeof onDone === 'function') onDone();
            }}
          >
            Continuar
          </motion.button>
        )}
      </motion.div>
    </div>
  );
};

export default TutorialCelebration;
